import { FC, forwardRef } from 'react';

import cn from '@/utils/cn';

import ErrorMessage from './ErrorMessage';
import { TextareaProps, TextareaWrapperProps } from './Textarea.types';
import { cva } from 'class-variance-authority';

export const textareaStyle = cva(
  `min-h-[162px] w-full rounded-md
  p-3.5 outline
  outline-1
  outline-dark-gray-100 placeholder:text-dark-gray-200 focus:outline-blue-600 focus:ring-4
  focus:ring-primary/20 text-[15px] leading-[24px]`,
  {
    variants: {
      error: {
        true: ['outline-red-400'],
      },
      success: {
        true: [
          'outline-green-500 hover:outline-green-500 focus:outline-green-500',
        ],
      },
      disabled: {
        true: [
          'cursor-not-allowed bg-gray-200 text-dark-gray-400 outline-dark-gray-100 hover:outline-dark-gray-100 focus:outline-dark-gray-100',
        ],
      },
    },
  }
);

const Textarea = forwardRef<HTMLTextAreaElement, Omit<TextareaProps, 'label'>>(
  (
    {
      className,
      error,
      success,
      disabled,
      rightSection,
      classNameRightSection,
      value,
      rows = 5,
      ...props
    },
    ref
  ) => (
    <TextareaWrapper
      error={error}
      classNameRightSection={classNameRightSection}
      success={success}
      rightSection={rightSection}
    >
      <textarea
        className={cn(
          textareaStyle({
            error: !!error,
            success: !!success,
            disabled: disabled,
          }),
          className
        )}
        disabled={disabled}
        ref={ref}
        value={value}
        rows={rows}
        {...props}
      />
    </TextareaWrapper>
  )
);

const TextareaWrapper: FC<Omit<TextareaWrapperProps, 'value' | 'label'>> = ({
  error,
  success,
  rightSection,
  classNameRightSection,
  children,
}) => (
  <>
    <div className={cn('group relative rounded-md')}>
      {rightSection && (
        <div className={cn('absolute right-3.5 top-4', classNameRightSection)}>
          {rightSection}
        </div>
      )}
      {children}
      {error && !success && <ErrorMessage errorMessage={error} />}
    </div>
  </>
);

Textarea.displayName = 'Textarea';

export { Textarea, TextareaWrapper };
